<script lang="ts" setup>
import { AbcHeading } from "@ordbogen-npm/abc-dsk";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import TgModal from "@/abc-dsk/components/Modal/TgModal.vue";
import { Size } from "@/abc-dsk/options";
import type { UnpackedBadge } from "@/common/types/vocab";
import AchievementBadge from "@/components/vocabularyTrainer/AchievementBadge.vue";

const props = withDefaults(
  defineProps<{
    badge: UnpackedBadge;
    isOpen?: boolean;
  }>(),
  { isOpen: false }
);

const emit = defineEmits(["close", "redeem"]);

const { t } = useI18n();

enum ModalState {
  Initial = 0,
  Opened = 1
}

const state = ref<ModalState>(ModalState.Initial);

let shakeInterval: number | null = null;
const isShaking = ref<boolean>(false);
const shakeDelay = 3000;
const shakeDuration = 450;

const queueShakeStop = (): void => {
  setTimeout(() => (isShaking.value = false), shakeDuration);
};
const stopShakeInterval = (): void => {
  if (shakeInterval !== null) {
    clearInterval(shakeInterval);
  }
};

const modal = ref<HTMLElement | null>(null);
const modalHeight = ref<string>("");

watch(
  () => props.isOpen,
  (isOpen) => {
    if (!isOpen) {
      return;
    }

    // Delay first shake a bit to allow modal opening animation to finish
    setTimeout(() => {
      isShaking.value = true;
      queueShakeStop();

      // Shake on delay
      shakeInterval = setInterval(() => {
        isShaking.value = true;
        queueShakeStop();
      }, shakeDuration + shakeDelay);
    }, 400);
  }
);

const isOpening = ref<boolean>(false);
const onRedeem = () => {
  stopShakeInterval();
  isOpening.value = true;
  modalHeight.value = modal.value?.clientHeight ? `${modal.value.clientHeight}px` : "";
  emit("redeem");

  setTimeout(() => {
    state.value = ModalState.Opened;
  }, 1000);
};

const onClose = () => {
  isShaking.value = false;
  isOpening.value = false;
  modalHeight.value = "";
  state.value = ModalState.Initial;
  stopShakeInterval();

  emit("close");
};

const badgeColorClass = computed(() => `badge-text--${props.badge.color}`);
</script>

<template>
  <TgModal :size="Size.Md" :is-open="isOpen" :lazy="false" @close="onClose()">
    <div ref="modal" class="badge-redemption-modal" :style="{ height: modalHeight }">
      <template v-if="state === ModalState.Initial">
        <div
          class="badge-redemption-modal__gift"
          :class="{ 'badge-redemption-modal__gift--shake': isShaking }"
        >
          <img
            src="@/assets/img/vocab/gift-top.png"
            class="badge-redemption-modal__gift-top"
            :class="{ 'badge-redemption-modal__gift-top--open': isOpening }"
            alt=""
          />
          <img
            src="@/assets/img/vocab/gift-bottom.png"
            class="badge-redemption-modal__gift-bottom"
            :class="{ 'badge-redemption-modal__gift-bottom--open': isOpening }"
            alt=""
          />
        </div>
        <TgButton
          class="badge-redemption-modal__redeem-button"
          :class="{ 'badge-redemption-modal__redeem-button--clicked': isOpening }"
          size="lg"
          @click="onRedeem()"
        >
          {{ t("vocab.achievements.item.openButton") }}
        </TgButton>
      </template>
      <div
        class="badge-redemption-modal__badge"
        :class="{ 'badge-redemption-modal__badge--open': isOpening }"
      >
        <AchievementBadge :badge="badge" xl />
      </div>
      <div
        class="badge-redemption-modal__open-text"
        :class="{ 'badge-redemption-modal__open-text--open': state === ModalState.Opened }"
      >
        <AbcHeading
          class="badge-redemption-modal__open-text-heading"
          color="black"
          tag="h1"
          size="md"
        >
          {{ t("vocab.achievements.redemptionModal.congratulations") }}
        </AbcHeading>
        <p class="badge-redemption-modal__open-text-subtext">
          <i18n-t keypath="vocab.achievements.redemptionModal.redeemedText">
            <template #badgeName>
              <span :class="['badge-text', badgeColorClass]">{{
                t("vocab.achievements.redemptionModal.badgeName", { name: badge.displayName })
              }}</span>
            </template>
          </i18n-t>
        </p>
        <TgButton class="badge-redemption-modal__back-button" size="lg" @click="onClose()">
          {{ t("vocab.achievements.redemptionModal.backToMissions") }}
        </TgButton>
      </div>
    </div>
  </TgModal>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.badge-redemption-modal {
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  gap: 60px;
  padding: 30px 15px 30px;

  #media-breakpoint-up(md, {
    gap: 110px;
    padding: 60px 120px 60px;
    overflow: hidden;
  });

  &__gift {
    display: flex;
    z-index: 1;
    flex-direction: column;
    align-items: center;
    width: 100%;

    &--shake {
      animation: shake 450ms ease-in-out;
    }

    &-top {
      z-index: 1;
      width: 100%;
      object-fit: contain;
      box-shadow: 0 25px 10px -10px rgba(0, 0, 0, 0.25);

      &--open {
        animation:
          shake-open 450ms ease-out,
          open-top 1s 550ms ease-out forwards;
      }
    }

    &-bottom {
      z-index: 0;
      width: 95%;
      object-fit: contain;
      margin-top: -5px;
      margin-left: 5px;

      &--open {
        animation:
          shake-open 450ms linear,
          open-bottom 1s 550ms ease-out forwards;
      }
    }
  }

  &__redeem-button--clicked {
    // Slightly longer delay so it looks like the bottom part of the box is "hitting" it :D
    animation: open-bottom 550ms 550ms forwards;
    pointer-events: none;
  }

  &__badge {
    position: absolute;
    top: 15%;
    left: 50%;
    transform: translate(-20%, -50%);
    opacity: 0;

    #media-breakpoint-up(md, {
      top: 28%;
    });

    &--open {
      animation: open-badge 1s 400ms ease-out forwards;
    }
  }

  &__open-text {
    position: absolute;
    margin: auto 0;
    transition: opacity 500ms ease-in-out;
    opacity: 0;
    text-align: center;

    &--open {
      position: initial;
      opacity: 1;

      .badge-redemption-modal__open-text-heading {
        margin-top: 30px;
        margin-bottom: 10px;

        #media-breakpoint-up(md, {
          margin-top: 75px;
        });
      }

      .badge-redemption-modal__back-button {
        margin-top: 40px;
      }
    }
  }
}

.badge-text {
  each(@theme-colors, {
    @name: replace(@key, "@", "");

    &--@{name} {
      color: rgb(@value[base]);
    }
  });
}

@keyframes shake {
  10%,
  90% {
    transform: translate3d(-3px, 0, 0);
  }

  20%,
  80% {
    transform: translate3d(4px, 0, 0);
  }

  30%,
  50%,
  70% {
    transform: translate3d(-6px, 0, 0);
  }

  40%,
  60% {
    transform: translate3d(6px, 0, 0);
  }
}

@keyframes shake-open {
  10%,
  90% {
    transform: translate3d(-3px, 0, 0);
  }

  20%,
  80% {
    transform: translate3d(4px, 0, 0);
  }

  30%,
  50%,
  70% {
    transform: translate3d(-6px, 0, 0);
  }

  35%,
  65% {
    transform: translate3d(11px, 0, 0);
  }

  45%,
  55% {
    transform: translate3d(-11px, 0, 0);
  }

  40%,
  60% {
    transform: translate3d(6px, 0, 0);
  }
}

@keyframes open-top {
  0% {
    transform: translate3d(0, 0, 0);
  }

  100% {
    transform: translate3d(0, -100vh, 0);
  }
}

@keyframes open-bottom {
  0% {
    transform: translate3d(0, 0, 0);
  }

  100% {
    transform: translate3d(0, 100vh, 0);
  }
}

@keyframes open-badge {
  0% {
    transform: translate(-50%, 60%);
  }

  100% {
    transform: translate(-50%, -50%);
    opacity: 1;
  }
}
</style>
